import { useEffect, useMemo } from "react";

import { flatMap, uniq } from "lodash/fp";

import { FILTER_CHIPS_BY_KIND } from "@app/analytics/constants/filterChips";
import useSearchMeetingsStore from "@app/analytics/hooks/meetings/useSearchMeetingsStore";
import useRollupContext from "@app/analytics/hooks/meetops/useRollupContext";
import useDateRangeStore from "@app/analytics/stores/useDateRangeStore";
import type { FilterChipKind, SearchRequestParams } from "@app/analytics/types";
import { getDateRanges } from "@app/analytics/utils";
import useAuth from "@app/common/auth/useAuth";
import useWorkspace from "@app/common/workspaces/useWorkspace";

import useSearchMeetingsQuery from "./useSearchMeetingsQuery";

const getCategoriesFromFilterChipKinds = (
  filterChipKinds: Set<FilterChipKind>
) =>
  uniq(
    flatMap(
      (kind: FilterChipKind) => FILTER_CHIPS_BY_KIND[kind].categories,
      Array.from(filterChipKinds)
    )
  );

const useSearchMeetings = () => {
  const { user } = useAuth();
  const { teamId, userId } = useRollupContext();
  const { workspaceId } = useWorkspace();

  const {
    setAllSearchResults,
    searchResults,
    setSearchResults,
    searchQuery,
    setSearchQuery,
    searchFilterChipKinds,
    setSearchFilterChipKinds,
    resetSearchFilterChipKinds,
    setIsSearching,
  } = useSearchMeetingsStore();

  const [dateRange] = useDateRangeStore(getDateRanges);

  const searchCategories = useMemo(
    () => getCategoriesFromFilterChipKinds(searchFilterChipKinds),
    [searchFilterChipKinds]
  );

  const searchParams: SearchRequestParams = {
    query: searchQuery,
    categories: searchCategories,
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    // only pass owner user id if we're searching meetings for
    // someone other than the current user, otherwise it'll get
    // narrowed down to only those that the current user owns
    ownerUserId: user?.id === userId ? undefined : userId,
    ownerTeamId: teamId,
    ownerWorkspaceId: workspaceId,
  };

  const query = useSearchMeetingsQuery(searchParams);

  useEffect(() => {
    resetSearchFilterChipKinds();
  }, [searchQuery]);

  useEffect(() => {
    if (query.isSuccess) {
      setSearchResults(query.results);
      setAllSearchResults(query.allResults);
    }
  }, [query.isSuccess, query.results, query.allResults]);

  useEffect(() => {
    setIsSearching(query.isFetching);
  }, [query.isFetching]);

  return {
    searchResults,
    searchQuery,
    setSearchQuery,
    searchFilterChipKinds,
    setSearchFilterChipKinds,
    query,
    isLoading: query.isLoading,
  };
};

export default useSearchMeetings;
